/**
 * Doctrine document model + applicability filtering — the "filter first, then RAG
 * over the eligible set only" contract (blueprint §14). Pure.
 *
 * Documents are tagged by jurisdiction/agency/locality/incident-type/stage/role/
 * status; retrieval must only ever consider documents that pass this filter, so
 * the assistant never surfaces locally-inappropriate doctrine.
 */

import type { IncidentStage } from './stages.js';
import type { Incident } from './incident.js';
import { activeIncidentTypes } from './incident.js';
import type { IncidentType } from './incident-type.js';

export type DoctrineStatus = 'current' | 'draft' | 'superseded';
export type Classification = 'unclassified' | 'official';

/** Tracks where a document is in the ingestion/embedding pipeline. */
export type IngestionStatus = 'pending' | 'ingesting' | 'ingested' | 'failed';

export interface DoctrineApplicability {
  states?: string[];
  agencies?: string[];
  districts?: string[];
  /** Empty/undefined = applies to all localities. */
  localities?: string[];
  /** Canonical incident-type tags, e.g. "StructureFire". */
  incidentTypes?: string[];
  stages?: IncidentStage[];
  roles?: string[];
  /** Response levels, e.g. 'Level1' | 'Level2' | 'Level3'. */
  levels?: string[];
}

export interface DoctrineDocument {
  id: string;
  title: string;
  type: string; // 'SOP' | 'Guideline' | 'Service Standard' | ...
  version: string;
  status: DoctrineStatus;
  owner?: string;
  /** POC docs are synthetic stand-ins. */
  provenance: 'synthetic' | 'official';
  standIn: boolean;
  classification: Classification;
  applicability: DoctrineApplicability;
  /** Plain-text / markdown body (chunked + embedded for RAG). */
  body: string;
  /** ISO-8601 — when this doc was uploaded/registered. */
  uploadedAt?: string;
  /** Ingestion/embedding pipeline state. */
  ingestionStatus?: IngestionStatus;
  /** ISO-8601 — when ingestion last completed or failed. */
  ingestedAt?: string;
}

export interface ApplicabilityQuery {
  state?: string;
  /** All agencies involved (lead + supporting). */
  agencies?: string[];
  locality?: string;
  /**
   * Every incident type currently believed active (primary + any co-active
   * additional types — see `activeIncidentTypes`). A doc matches if it's
   * untyped (applies to all types — e.g. type-agnostic water-supply doctrine)
   * or shares at least one type with this list, so doctrine for a type added
   * mid-incident (a car fire that spreads into grass) becomes eligible
   * without needing the incident's primary type changed.
   */
  incidentTypes?: string[];
  stage?: IncidentStage;
  role?: string;
  /** Include draft documents (default false). */
  includeDraft?: boolean;
}

/**
 * Canonical incident-type tag for an incident — its primary type only
 * (`typeConfirmed ?? typeReported`). For applicability matching against every
 * currently-active type (including co-active additional types), use
 * `activeIncidentTypes()` instead.
 */
export function incidentTypeTag(incident: Incident): IncidentType {
  return incident.typeConfirmed ?? incident.typeReported;
}

/** Build an applicability query from the current incident. */
export function queryForIncident(incident: Incident, role?: string): ApplicabilityQuery {
  return {
    state: 'NSW',
    agencies: [incident.leadAgency, ...incident.supportingAgencies],
    locality: incident.locality,
    incidentTypes: activeIncidentTypes(incident),
    stage: incident.stage,
    role,
  };
}

// A doc dimension matches if the doc does not constrain it, or the query value
// is within the doc's allowed set. All constrained dimensions must match.
const dimMatches = (allowed: string[] | undefined, value: string | undefined): boolean => {
  if (!allowed || allowed.length === 0) return true; // doc applies broadly
  if (!value) return false; // doc constrains it but we don't know → exclude
  return allowed.map((a) => a.toLowerCase()).includes(value.toLowerCase());
};

const anyMatches = (allowed: string[] | undefined, values: string[] | undefined): boolean => {
  if (!allowed || allowed.length === 0) return true;
  if (!values || values.length === 0) return false;
  const set = new Set(allowed.map((a) => a.toLowerCase()));
  return values.some((v) => set.has(v.toLowerCase()));
};

/** True if a document is eligible for the given incident context. */
export function applicabilityMatches(doc: DoctrineDocument, q: ApplicabilityQuery): boolean {
  if (doc.status === 'superseded') return false;
  if (doc.status === 'draft' && !q.includeDraft) return false;
  const a = doc.applicability;
  return (
    dimMatches(a.states, q.state) &&
    anyMatches(a.agencies, q.agencies) &&
    dimMatches(a.localities, q.locality) &&
    anyMatches(a.incidentTypes, q.incidentTypes) &&
    (!a.stages || a.stages.length === 0 || (q.stage != null && a.stages.includes(q.stage))) &&
    dimMatches(a.roles, q.role)
  );
}

/** The eligible document set for a query (RAG must only use these). */
export function filterApplicable(
  docs: readonly DoctrineDocument[],
  q: ApplicabilityQuery,
): DoctrineDocument[] {
  return docs.filter((d) => applicabilityMatches(d, q));
}
